import React, { useEffect, useState } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight, ExternalLink, Globe2, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { previewMarketingLinkedIn, postMarketingLinkedInNow, updateMarketingArticle } from '../../services/api';

const PageViewer = ({ pages }) => {
  const [i, setI] = useState(0);
  return (
    <div className="relative bg-slate-100" data-testid="post-preview-carousel">
      <img src={pages[i]} alt={`Page ${i + 1}`} className="w-full block" data-testid="post-preview-page" />
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-2 py-0.5 rounded" data-testid="post-preview-page-count">{i + 1} / {pages.length}</div>
      {i > 0 && (
        <button type="button" onClick={() => setI(i - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-1.5 shadow" data-testid="post-preview-prev">
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}
      {i < pages.length - 1 && (
        <button type="button" onClick={() => setI(i + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-1.5 shadow" data-testid="post-preview-next">
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

// LinkedIn-style mock-up of the company-page post, with editable caption and Post now.
export const LinkedInPostDialog = ({ open, onOpenChange, articleId, ensureSaved, onPosted }) => {
  const [data, setData] = useState(null);
  const [caption, setCaption] = useState('');
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [repostOk, setRepostOk] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData(null); setRepostOk(false); setLoading(true);
    (async () => {
      if (!(await ensureSaved())) { onOpenChange(false); return; }
      try {
        const res = await previewMarketingLinkedIn(articleId);
        setData(res); setCaption(res.caption || '');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not build the post preview');
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const existing = (data?.existingPosts || []).filter((p) => p.status === 'sent');
  const inFlight = (data?.existingPosts || []).some((p) => p.status !== 'sent');
  const canPost = data?.ready && data?.linkedinReady && caption.trim() && !inFlight && (!existing.length || repostOk);

  // Keep caption edits even when closing without posting.
  const close = async (next) => {
    if (!next && data && !posting && caption.trim() && caption.trim() !== (data.caption || '').trim()) {
      try {
        await updateMarketingArticle(articleId, { linkedinCaption: caption.trim() });
        toast.success('Caption saved');
      } catch { toast.error('Could not save the caption'); }
    }
    onOpenChange(next);
  };

  const postNow = async () => {
    const msg = data.articleStatus === 'published'
      ? 'Post this carousel to the Lutrom Engineering LinkedIn page now?'
      : 'This article is not live yet. It will be published to the website first, then posted to LinkedIn. Continue?';
    if (!window.confirm(msg)) return;
    setPosting(true);
    try {
      const out = await postMarketingLinkedInNow(articleId, { caption: caption.trim(), allowRepost: repostOk });
      if (out.status === 'sent') {
        toast.success(out.publishedToWebsite ? 'Published to the website and posted to LinkedIn' : 'Posted to LinkedIn');
        onPosted?.(out);
        onOpenChange(false);
      } else {
        toast.error(`${out.message || 'LinkedIn post did not complete'}${out.error ? ` — ${out.error}` : ''}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Post failed');
    } finally {
      setPosting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="linkedin-post-dialog">
        <DialogHeader>
          <DialogTitle>LinkedIn post preview</DialogTitle>
          <DialogDescription>This is what will be posted to the Lutrom Engineering Company Page.</DialogDescription>
        </DialogHeader>
        {loading || !data ? (
          <div className="py-16 text-center text-slate-500 text-sm"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Building preview…</div>
        ) : (
          <div className="space-y-4">
            {!data.ready && (
              <div className="rounded bg-red-50 border border-red-200 p-2 text-sm text-red-800" data-testid="post-preview-errors">
                <div className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4" /> Posting is blocked</div>
                <ul className="list-disc pl-5 text-xs mt-1 space-y-0.5">{data.errors.map((e) => <li key={e}>{e}</li>)}</ul>
              </div>
            )}
            <div className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-sm" data-testid="post-preview-card">
              <div className="flex items-center gap-3 p-3">
                <div className="h-10 w-10 rounded bg-slate-900 text-white flex items-center justify-center text-sm font-bold">LE</div>
                <div className="text-sm leading-tight">
                  <div className="font-semibold text-slate-900">Lutrom Engineering</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1">Now · <Globe2 className="h-3 w-3" /></div>
                </div>
              </div>
              <Textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={4}
                className="mx-3 mb-3 w-[calc(100%-1.5rem)] text-sm"
                placeholder="Write the post caption"
                data-testid="post-preview-caption"
              />
              <div className="text-[11px] text-slate-500 px-3 -mt-2 mb-2">
                {caption.length} characters · saved with the article when you post
              </div>
              <PageViewer pages={data.pages} />
              <div className="px-3 py-2 text-xs text-slate-600 border-t border-slate-100 truncate" data-testid="post-preview-link">
                {data.articleUrl}
              </div>
            </div>
            {!data.linkedinReady && (
              <p className="text-xs text-red-700" data-testid="post-preview-linkedin-not-ready">
                {data.linkedinNotReadyReason} Connect it in Marketing → Settings to post.
              </p>
            )}
            {data.articleStatus !== 'published' && (
              <p className="text-xs text-amber-700" data-testid="post-preview-will-publish">
                Not live on the website yet — Post now publishes it first so the link works.
              </p>
            )}
            {inFlight && (
              <p className="text-xs text-orange-700" data-testid="post-preview-inflight">
                A LinkedIn post for this article is in progress or has an unknown outcome. Check the Schedule page first.
              </p>
            )}
            {existing.length > 0 && (
              <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900 space-y-2" data-testid="post-preview-existing">
                <div>
                  Already posted {existing.length === 1 ? 'once' : `${existing.length} times`}.
                  {existing[0].externalUrl && (
                    <a href={existing[0].externalUrl} target="_blank" rel="noreferrer" className="ml-1 underline inline-flex items-center gap-1">
                      View latest <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  {' '}The earlier post is not changed or deleted.
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={repostOk} onCheckedChange={(v) => setRepostOk(!!v)} data-testid="post-preview-repost-ack" />
                  I understand this creates a second LinkedIn post
                </label>
              </div>
            )}
          </div>
        )}
        <DialogFooter className="sticky -bottom-6 -mx-6 -mb-6 px-6 py-3 bg-white border-t border-slate-100 z-10">
          <Button variant="outline" onClick={() => close(false)} data-testid="post-preview-close">Close</Button>
          <Button onClick={postNow} disabled={!canPost || posting} className="bg-[#0a66c2] hover:bg-[#004182]" data-testid="post-now-confirm-btn">
            {posting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
            Post now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkedInPostDialog;
